import { useEffect } from "react";
import { BrowserRouter, Navigate, Route, Routes, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import Accueil from "@/pages/Accueil";
import Reserver from "@/pages/Reserver";
import ReservationSuivi from "@/pages/ReservationSuivi";
import ReservationConfirmation from "@/pages/ReservationConfirmation";
import AdminReservations from "@/pages/AdminReservations";
import Merci from "@/pages/Merci";
import Confirmation from "@/pages/Confirmation";
import Connexion from "@/pages/Connexion";
import MotDePasseOublie from "@/pages/MotDePasseOublie";
import ReinitialiserMotDePasse from "@/pages/ReinitialiserMotDePasse";
import InscriptionEcole from "@/pages/InscriptionEcole";
import Ambassadeur from "@/pages/Ambassadeur";
import AmbassadeurDashboard from "@/pages/AmbassadeurDashboard";
import ConditionsRecompenses from "@/pages/ConditionsRecompenses";
import BonCarburant from "@/pages/BonCarburant";
import AdminWaitlist from "@/pages/AdminWaitlist";
import AdminBons from "@/pages/AdminBons";
import ProDisponibilites from "@/pages/ProDisponibilites";
import AdminAmbassadors from "@/pages/AdminAmbassadors";
import AdminAmbassadorDetail from "@/pages/AdminAmbassadorDetail";
import ProReservations from "@/pages/ProReservations";
import AdminCommissions from "@/pages/AdminCommissions";
import QualifDashboard from "@/pages/QualifDashboard";
import ProProfil from "@/pages/ProProfil";
import ProAffiches from "@/pages/ProAffiches";
import AdminLeads from "@/pages/AdminLeads";
import AdminSchools from "@/pages/AdminSchools";
import AdminSchoolsMap from "@/pages/AdminSchoolsMap";
import AdminPartners from "@/pages/AdminPartners";
import AdminEmails from "@/pages/AdminEmails";
import AdminSettings from "@/pages/AdminSettings";
import AdminZones from "@/pages/AdminZones";
import AdminDuplicates from "@/pages/AdminDuplicates";
import AdminAffiches from "@/pages/AdminAffiches";
import Permis from "@/pages/Permis";
import PermisAccelere from "@/pages/PermisAccelere";
import RepasserPermis from "@/pages/RepasserPermis";
import TransfertAutoEcole from "@/pages/TransfertAutoEcole";
import AdminLeadDetail from "@/pages/AdminLeadDetail";
import AdminReminders from "@/pages/AdminReminders";
import AdminCampaigns from "@/pages/AdminCampaigns";
import AdminCRM from "@/pages/AdminCRM";
import AdminReviews from "@/pages/AdminReviews";
import CGU from "@/pages/legal/CGU";
import CGV from "@/pages/legal/CGV";
import MentionsLegales from "@/pages/legal/MentionsLegales";
import Confidentialite from "@/pages/legal/Confidentialite";
import QuiSommesNous from "@/pages/QuiSommesNous";
import Contact from "@/pages/Contact";
import Blog from "@/pages/Blog";
import BlogArticle from "@/pages/BlogArticle";
import { CookieConsent } from "@/components/CookieConsent";
import { initPixels, syncClarityRoute } from "@/lib/pixels";
import SitePopupManager from "@/components/SitePopup";
import AdminSite from "@/pages/AdminSite";

const homeFor = (role) => (role === "admin" ? "/admin" : role === "qualificateur" ? "/qualif" : "/pro");

const Guard = ({ role, children }) => {
  const { user } = useAuth();
  if (user === null) return <div className="grid min-h-screen place-items-center text-neutral-500">Chargement…</div>;
  if (user === false) return <Navigate to="/connexion" replace />;
  if (role && user.role !== role) return <Navigate to={homeFor(user.role)} replace />;
  return children;
};

// Suit la navigation : coupe Microsoft Clarity sur les pages admin / auto-école.
function RouteWatcher() {
  const { pathname } = useLocation();
  useEffect(() => { syncClarityRoute(pathname); }, [pathname]);
  return null;
}

function App() {
  useEffect(() => {
    initPixels();
  }, []);

  return (
    <AuthProvider>
      <BrowserRouter>
        <RouteWatcher />
        <SitePopupManager />
        <Routes>
          <Route path="/" element={<Accueil />} />
          <Route path="/permis/:ville" element={<Accueil />} />
          <Route path="/merci" element={<Merci />} />
          <Route path="/confirmation/:token" element={<Confirmation />} />
          <Route path="/connexion" element={<Connexion />} />
          <Route path="/mot-de-passe-oublie" element={<MotDePasseOublie />} />
          <Route path="/reinitialiser-mot-de-passe" element={<ReinitialiserMotDePasse />} />
          <Route path="/inscription-auto-ecole" element={<InscriptionEcole />} />
          <Route path="/permis" element={<Permis />} />
          <Route path="/permis-accelere" element={<PermisAccelere />} />
          <Route path="/repasser-permis" element={<RepasserPermis />} />
          <Route path="/transfert-auto-ecole" element={<TransfertAutoEcole />} />
          <Route path="/blog" element={<Blog />} />
          <Route path="/blog/:slug" element={<BlogArticle />} />
          <Route path="/ambassadeur" element={<Ambassadeur />} />
          <Route path="/ambassadeur/dashboard" element={<AmbassadeurDashboard />} />
          {/* Demande de place — service 100% gratuit (ancien tunnel payant devenu formulaire de lead). */}
          <Route path="/reserver" element={<Reserver />} />
          <Route path="/reservation/confirmation" element={<ReservationConfirmation />} />
          <Route path="/reservation/:rid" element={<ReservationSuivi />} />
          <Route path="/conditions-recompenses" element={<ConditionsRecompenses />} />
          <Route path="/bon-carburant" element={<BonCarburant />} />
          <Route path="/cgu" element={<CGU />} />
          <Route path="/cgv" element={<CGV />} />
          <Route path="/mentions-legales" element={<MentionsLegales />} />
          <Route path="/confidentialite" element={<Confidentialite />} />
          <Route path="/qui-sommes-nous" element={<QuiSommesNous />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/pro" element={<Guard role="school"><ProReservations /></Guard>} />
          <Route path="/pro/reservations" element={<Navigate to="/pro" replace />} />
          <Route path="/qualif" element={<Guard role="qualificateur"><QualifDashboard /></Guard>} />
          <Route path="/pro/disponibilites" element={<Guard role="school"><ProDisponibilites /></Guard>} />
          <Route path="/pro/annonces" element={<Guard role="school"><ProAffiches /></Guard>} />
          <Route path="/pro/profil" element={<Guard role="school"><ProProfil /></Guard>} />
          <Route path="/admin" element={<Guard role="admin"><AdminLeads /></Guard>} />
          <Route path="/admin/reservations" element={<Guard role="admin"><AdminReservations /></Guard>} />
          <Route path="/admin/liste-attente" element={<Guard role="admin"><AdminWaitlist /></Guard>} />
          <Route path="/admin/leads/:leadId" element={<Guard role="admin"><AdminLeadDetail /></Guard>} />
          <Route path="/admin/auto-ecoles" element={<Guard role="admin"><AdminSchools /></Guard>} />
          <Route path="/admin/carte" element={<Guard role="admin"><AdminSchoolsMap /></Guard>} />
          <Route path="/admin/partenaires" element={<Guard role="admin"><AdminPartners /></Guard>} />
          <Route path="/admin/doublons" element={<Guard role="admin"><AdminDuplicates /></Guard>} />
          <Route path="/admin/zones" element={<Guard role="admin"><AdminZones /></Guard>} />
          <Route path="/admin/affiches" element={<Guard role="admin"><AdminAffiches /></Guard>} />
          <Route path="/admin/ambassadeurs" element={<Guard role="admin"><AdminAmbassadors /></Guard>} />
          <Route path="/admin/ambassadeurs/:id" element={<Guard role="admin"><AdminAmbassadorDetail /></Guard>} />
          <Route path="/admin/bon-carburant" element={<Guard role="admin"><AdminBons /></Guard>} />
          <Route path="/admin/rappels" element={<Guard role="admin"><AdminReminders /></Guard>} />
          <Route path="/admin/campagnes" element={<Guard role="admin"><AdminCampaigns /></Guard>} />
          <Route path="/admin/crm" element={<Guard role="admin"><AdminCRM /></Guard>} />
          <Route path="/admin/avis" element={<Guard role="admin"><AdminReviews /></Guard>} />
          <Route path="/admin/emails" element={<Guard role="admin"><AdminEmails /></Guard>} />
          <Route path="/admin/reglages" element={<Guard role="admin"><AdminSettings /></Guard>} />
          <Route path="/admin/site" element={<Guard role="admin"><AdminSite /></Guard>} />
          {/* Modules retirés (inscriptions/commissions/facturation, 2ᵉ passage) -> admin */}
          <Route path="/admin/commissions" element={<Guard role="admin"><AdminCommissions /></Guard>} />
          <Route path="/admin/inscriptions" element={<Navigate to="/admin" replace />} />
          <Route path="/admin/facturation" element={<Navigate to="/admin" replace />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        <CookieConsent />
      </BrowserRouter>
      <Toaster position="top-right" />
    </AuthProvider>
  );
}

export default App;
